import { z } from "zod";

import { zRichText } from "@/utils/types";

// Define the standings calculation types
// Helper to handle optional number fields that can be empty strings
const optionalNumber = z
    .union([
        z.number(),
        z.string().transform(val => {
            if (val === "" || val === undefined) return null;
            const num = parseInt(val);
            return isNaN(num) ? null : num;
        }),
        z.null(),
        z.undefined(),
    ])
    .transform(val => (val === undefined ? null : val));

export const zStandingsCalculationInput = z
    .object({
        type: z.enum(["manual", "points", "rank"]),
        tournamentCap: optionalNumber.optional(),
        playerRankCap: optionalNumber.optional(),
        minPoints: z.number().min(0, "Min points must be 0 or greater").optional(),
        rankPointsString: z.string().optional(),
    })
    .refine(
        data => {
            if (data.type === "rank") {
                return data.rankPointsString && data.rankPointsString.length > 0;
            }
            return true;
        },
        {
            message: "Rank points are required for rank method",
            path: ["rankPointsString"],
        }
    );

export type StandingsCalculationInput = z.infer<typeof zStandingsCalculationInput>;

export const zTournamentSeriesInput = z.object({
    title: z.string().min(1, "Title is required"),
    description: zRichText.nullable(),
    startAt: z
        .string()
        .or(z.date())
        .transform(arg => new Date(arg)),
    endAt: z
        .string()
        .or(z.date())
        .transform(arg => new Date(arg)),
    heroImageId: z.string().nullable(),
    thumbnailImageId: z.string().nullable(),
    previousWinnerPlayerId: z.string().nullable(),
    isInternalOnly: z.boolean(),
    isHidden: z.boolean(),
    standingsInfo: z.string().nullable(),
    standingsCalculation: zStandingsCalculationInput.nullable().default({ type: "manual" }),
});

export type TournamentSeriesInput = z.infer<typeof zTournamentSeriesInput>;
